import React, { useState, useEffect, useRef } from 'react'
import { useMusicPlayer } from './MusicPlayerContext.jsx'
import { useCamera } from './SimpleCameraSystem.jsx'
import { useOverlayManager } from './overlayManager.jsx'
import infoData from './infoData.json'
import './unifiedUI.scss'

const InvisibleHeader = ({ audioEnabled, setAudioEnabled }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false)
  const [showReturnTooltip, setShowReturnTooltip] = useState(false)
  const [showOutsideTooltip, setShowOutsideTooltip] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Timeout refs for auto-close functionality
  const dropdownTimeoutRef = useRef(null)
  const burgerTimeoutRef = useRef(null)
  const { 
    isPlaying, 
    togglePlayPause, 
    resetAutoplayState, 
    currentTrack, 
    nextTrack, 
    previousTrack 
  } = useMusicPlayer()
  const { 
    showConversation, 
    showGallery, 
    showMusic, 
    showContact, 
    showAbout,
    showCredits,
    currentCameraState,
    goBack,
    enterOffice,
    cameraControlsRef,
    setVisibleInteractions
  } = useCamera()
  
  const { activeOverlay, showOverlay } = useOverlayManager()
  
  const [musicEnabled, setMusicEnabled] = useState(true)
  
  // Hide audio controls when any overlay is open
  const isAnyOverlayOpen = activeOverlay !== null

  // Auto-close dropdown after 5 seconds of inactivity
  useEffect(() => {
    if (isDropdownOpen) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setIsDropdownOpen(false)
      }, 5000)
    } else {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }

    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }
  }, [isDropdownOpen])

  // Auto-close burger menu after 5 seconds of inactivity
  useEffect(() => {
    if (isBurgerMenuOpen) {
      burgerTimeoutRef.current = setTimeout(() => {
        setIsBurgerMenuOpen(false)
      }, 5000)
    } else {
      if (burgerTimeoutRef.current) {
        clearTimeout(burgerTimeoutRef.current)
      }
    }

    return () => {
      if (burgerTimeoutRef.current) {
        clearTimeout(burgerTimeoutRef.current)
      }
    }
  }, [isBurgerMenuOpen])

  // Reset dropdown timeout when user interacts
  const resetDropdownTimeout = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    if (isDropdownOpen) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setIsDropdownOpen(false)
      }, 5000)
    }
  }

  // Reset burger menu timeout when user interacts
  const resetBurgerTimeout = () => {
    if (burgerTimeoutRef.current) {
      clearTimeout(burgerTimeoutRef.current)
    }
    if (isBurgerMenuOpen) {
      burgerTimeoutRef.current = setTimeout(() => {
        setIsBurgerMenuOpen(false)
      }, 5000)
    }
  }

  const toggleDropdown = () => {
    if (!isDropdownOpen) {
      // Close burger menu if it's open
      setIsBurgerMenuOpen(false)
    }
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleMusicToggle = () => {
    resetDropdownTimeout() // Reset timeout on interaction
    
    const newMusicEnabled = !musicEnabled
    setMusicEnabled(newMusicEnabled)
    
    if (!newMusicEnabled && isPlaying) {
      // If turning music off and it's playing, pause it
      togglePlayPause()
    }
    
    if (!newMusicEnabled) {
      // Reset autoplay state when music is disabled
      resetAutoplayState()
    }
  }

  const handleAudioToggle = () => {
    resetDropdownTimeout() // Reset timeout on interaction
    setAudioEnabled(!audioEnabled)
  }

  const toggleBurgerMenu = () => {
    if (!isBurgerMenuOpen) {
      // Close audio dropdown if it's open
      setIsDropdownOpen(false)
    }
    setIsBurgerMenuOpen(!isBurgerMenuOpen)
  }

  const handleNavigation = (overlay) => {
    resetBurgerTimeout() // Reset timeout on interaction
    
    // Close burger menu first
    setIsBurgerMenuOpen(false)
    
    // Handle resume download separately
    if (overlay === 'resume') {
      window.open('/documents/resume-bjoernGrauers-2025.pdf', '_blank')
      return
    }
    
    // Handle credits separately (still uses camera system)
    if (overlay === 'credits') {
      // Credits might need special handling - keeping original logic for now
      return
    }
    
    // Open the selected overlay using overlay manager
    showOverlay(overlay)
  }

  return (
    <div className="invisible-header">
      {/* Button Set - hide when overlay is open */}
      {!isAnyOverlayOpen && (
        <div className="nav-container">
          <div className="wrapper-nav-top">
            {/* Left side: hamburger button */}
            <div className="btn-set">
              <button className="nav-btn" onClick={toggleBurgerMenu}>
                <span className="material-symbols-rounded">menu</span>
              </button>
              
              {/* Hamburger Menu Dropdown */}
              {isBurgerMenuOpen && (
                <div 
                  className="nav-dropdown"
                  onMouseEnter={resetBurgerTimeout}
                  onMouseLeave={resetBurgerTimeout}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setIsBurgerMenuOpen(false);
                    }
                  }}
                >
                  <button className="mobile-close-btn" onClick={() => setIsBurgerMenuOpen(false)}>✕</button>
                  
                  <div className="dropdown-item nav-item" onClick={() => handleNavigation('work')}>
                    <span>Work</span>
                  </div>
                  
                  <div className="dropdown-item nav-item" onClick={() => handleNavigation('about')}>
                    <span>About</span>
                  </div>
                  
                  <div className="dropdown-item nav-item" onClick={() => handleNavigation('music')}>
                    <span>Music</span>
                  </div>
                  
                  <div className="dropdown-item nav-item" onClick={() => handleNavigation('contact')}>
                    <span>Contact</span>
                  </div>

                  <div className="dropdown-item nav-item" onClick={() => handleNavigation('resume')}>
                    <span>Resume</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side: sound button */}
          <div className="btn-set">
            <button className="nav-btn" onClick={toggleDropdown}>
              <span className="material-symbols-rounded">volume_up</span>
            </button>

            {/* Audio Dropdown */}
            {isDropdownOpen && (
              <div 
                className="nav-dropdown nav-dropdown-right"
                onMouseEnter={resetDropdownTimeout}
                onMouseLeave={resetDropdownTimeout}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsDropdownOpen(false);
                  }
                }}
              >
                <button className="mobile-close-btn" onClick={() => setIsDropdownOpen(false)}>✕</button>
                
                {/* Music Player Section */}
                {musicEnabled && currentTrack && (
                  <div className="dropdown-item">
                    <div className="text-subheading-2" style={{ textAlign: 'center', marginBottom: 'var(--spacing-medium)' }}>
                      {currentTrack.title}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--spacing-small)' }}>
                      <button className="btn" onClick={() => { resetDropdownTimeout(); previousTrack(); }}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                        </svg>
                      </button>
                      <button className="btn" onClick={() => { resetDropdownTimeout(); togglePlayPause(); }}>
                        {isPlaying ? (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                      </button>
                      <button className="btn" onClick={() => { resetDropdownTimeout(); nextTrack(); }}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="dropdown-item">
                  <div className="toggle-row" onClick={handleMusicToggle}>
                    <span>Music</span>
                    <button className={`btn ${musicEnabled ? 'active' : ''}`}>
                      {musicEnabled ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                          <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="dropdown-item">
                  <div className="toggle-row" onClick={handleAudioToggle}>
                    <span>Audio</span>
                    <button className={`btn ${audioEnabled ? 'active' : ''}`}>
                      {audioEnabled ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.34-1.71-.71L7 9H4c-.55 0-1 .45-1 1zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.63 3.63c-.39.39-.39 1.02 0 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l4.18 4.18c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Return Button - office state */}
      {currentCameraState === 'office' && (
        <div className="scene-nav-button scene-nav-return">
          <button className="nav-btn" onClick={goBack}>
            <span className="material-symbols-rounded">arrow_forward</span>
          </button>
        </div>
      )}

      {/* Outside Button - lounge state */}
      {currentCameraState === 'lounge' && (
        <div className="scene-nav-button scene-nav-outside">
          <button className="nav-btn" onClick={() => {
            cameraControlsRef.current.moveToState?.('outside')
            setVisibleInteractions(['doorbell'])
          }}>
            <span className="material-symbols-rounded">arrow_forward</span>
          </button>
        </div>
      )}
    </div>
  )
}

export { InvisibleHeader }
